import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { environment } from 'src/environments/environment';
import { MarginExpense } from '../../models/margin-expense.model';
import { initialMarginExpensesSlice } from './margin-expenses.slice';
import { MarginExpenseType } from '../../models/margin-expense-type.model';
import { FacilityExpenseCaps } from 'src/app/shared/models/facility-expense-caps.model';
import { computed, Signal } from '@angular/core';
import { MarginExpenseTypes } from '../../enums/margin-expense-types.enum';
import { MarginExpenseTypesAttributes } from '../../enums/margin-expense-types-attributes.enum';

export const MarginExpensesStore = signalStore(
    { providedIn: 'root' },

    withState(initialMarginExpensesSlice),

    withComputed((store) => ({
        snapshot: computed(() => ({
            marginExpenses: store.marginExpenses()
        }))
    })),

    withComputed((store) => ({
        expenseTotalCost: computed(() => (marginExpenseTypeId: MarginExpenseTypes) => {
            return getExpenseTotalCost(store.marginExpenses, marginExpenseTypeId);
        })
    })),

    withMethods((store) => ({
        addMarginExpense: (marginExpenseList: MarginExpense[]) => {
            const marginExpenses = store.marginExpenses() ?? [];
            const doesMarginExpenseExist = marginExpenses.some(
                (e) =>
                    e.marginExpenseTypeId === marginExpenseList[0].marginExpenseTypeId &&
                    e.rowId === marginExpenseList[0].rowId
            );
            if (!doesMarginExpenseExist) {
                const updatedExpenses = [...marginExpenses, ...marginExpenseList];
                patchState(store, { marginExpenses: updatedExpenses });
            }
        },

        removeMarginExpense: (rowId: number) => {
            const updatedMarginExpenses = store.marginExpenses().filter((expense) => expense.rowId !== rowId);
            patchState(store, { marginExpenses: updatedMarginExpenses });
        },
        updateMarginExpensesFromData: (marginExpenses: MarginExpense[]) => {
            patchState(store, { marginExpenses: marginExpenses });
        },
        updateMarginExpenseField: (marginExpense: Partial<MarginExpense>) => {
            const updatedExpenses = store.marginExpenses().map((expense) => {
                if (
                    expense.marginExpenseTypeId === marginExpense.marginExpenseTypeId &&
                    expense.marginExpenseAttributeId === marginExpense.marginExpenseAttributeId &&
                    expense.rowId === marginExpense.rowId
                ) {
                    return { ...expense, ...marginExpense };
                }
                return expense;
            });
            patchState(store, { marginExpenses: updatedExpenses });
        },
        updateMarginExpenseFieldsForDelete: (
            marginExpenseTypeId: MarginExpenseTypes,
            isDeleted: boolean,
            rowId: number
        ) => {
            const updatedExpenses = store.marginExpenses().map((expense) => {
                if (expense.marginExpenseTypeId === marginExpenseTypeId && expense.rowId === rowId) {
                    return { ...expense, isDeleted: isDeleted };
                }
                return expense;
            });
            patchState(store, { marginExpenses: updatedExpenses });
        },
        getMarginExpenseTypes: (marginExpenseTypes: MarginExpenseType[]) => {
            patchState(store, { marginExpenseTypes: marginExpenseTypes });
        },

        getExpenseCaps: (expenseCaps: FacilityExpenseCaps[]) => {
            patchState(store, { expenseCaps: expenseCaps });
        },

        getState: () => store,

        getSnapshot: () => store.snapshot()
    })),

    environment.storeWithDevTools('locums-margin-calculator-expenses-store')
);

function getExpenseTotalCost(marginExpenses: Signal<MarginExpense[]>, marginExpenseTypeId: MarginExpenseTypes) {
    const findCurrentExpenseValue = (marginExpenseTypesAttributeId: MarginExpenseTypesAttributes) => {
        const expense = marginExpenses().find(
            (e) =>
                e.marginExpenseTypeId === marginExpenseTypeId &&
                e.marginExpenseAttributeId === marginExpenseTypesAttributeId &&
                !e.isDeleted
        );
        return expense?.value ?? '0';
    };

    switch (marginExpenseTypeId) {
        case MarginExpenseTypes.AIRFARE: {
            const amount = Number(findCurrentExpenseValue(MarginExpenseTypesAttributes.Amount));
            const roundTripFlights = Number(findCurrentExpenseValue(MarginExpenseTypesAttributes.RoundTripFlights));
            return (amount * roundTripFlights).toString();
        }
        case MarginExpenseTypes.HOUSING: {
            const nightlyCost = Number(findCurrentExpenseValue(MarginExpenseTypesAttributes.NightlyCost));
            const numberOfNights = Number(findCurrentExpenseValue(MarginExpenseTypesAttributes.Nights));
            return (nightlyCost * numberOfNights).toString();
        }
        case MarginExpenseTypes.RENTAL_CAR: {
            const dailyCost = Number(findCurrentExpenseValue(MarginExpenseTypesAttributes.DailyCost));
            const numberOfDays = Number(findCurrentExpenseValue(MarginExpenseTypesAttributes.Days));
            return (dailyCost * numberOfDays).toString();
        }
        default:
            return '0';
    }
}
